// src/components/connectors/connectors-ui.ts
//
// DEM-2118 (Marketing F4b) — vocabulário de UI + leitores puros de conectores.
//
// Constantes/funções compartilhadas entre ConnectorsPage e BusinessSocialTab.
// Fica FORA do arquivo do componente (ConnectModal) por causa da regra
// react-refresh/only-export-components: um arquivo de componente só exporta
// componentes. Aqui só há dados/funções puras — zero JSX.

import type {
  ConnectorCatalogItem,
  ConnectorAuthMode,
  ConnectorCategory,
} from '@/lib/api';

export const CATEGORY_LABELS: Record<ConnectorCategory, string> = {
  social_media: 'Social',
  storage: 'Storage',
  communication: 'Comunicação',
  crm: 'CRM',
  ads: 'Ads',
  analytics: 'Analytics',
  payments: 'Pagamentos',
  marketplace: 'Marketplace',
  other: 'Outro',
};

export const MODE_LABELS: Record<ConnectorAuthMode, string> = {
  api_key: 'API Key',
  oauth2: 'OAuth2',
  mcp: 'MCP',
};

export const CATALOG_STATUS: Record<
  string,
  { label: string; variant: 'success' | 'warning' | 'info' | 'default' }
> = {
  available: { label: 'Disponível', variant: 'success' },
  beta: { label: 'Beta', variant: 'warning' },
  coming_soon: { label: 'Em breve', variant: 'info' },
  deprecated: { label: 'Descontinuado', variant: 'default' },
};

export const CONN_STATUS: Record<
  string,
  { label: string; variant: 'success' | 'warning' | 'danger' | 'default' }
> = {
  connected: { label: 'Conectado', variant: 'success' },
  expired: { label: 'Expirado', variant: 'warning' },
  error: { label: 'Erro', variant: 'danger' },
  revoked: { label: 'Revogado', variant: 'default' },
};

/** Modos conectáveis (F3/DEM-2124 habilita oauth2 — antes era só api_key/mcp). */
export const CONNECTABLE_MODES: ConnectorAuthMode[] = ['api_key', 'oauth2', 'mcp'];

/** Lê o external_account com os campos capturados no callback OAuth. */
export function readExternalAccount(ext: Record<string, unknown> | null | undefined): {
  handle?: string;
  avatar?: string;
  profileUrl?: string;
  name?: string;
} {
  const e = ext ?? {};
  return {
    handle: (e['handle'] as string | undefined) ?? (e['account_id'] as string | undefined),
    avatar: e['avatar'] as string | undefined,
    profileUrl: e['profile_url'] as string | undefined,
    name: e['name'] as string | undefined,
  };
}

/** Lê o estado de budget do X-MCP gravado na connection (external_account.mcp). */
export function readXMcpBudget(ext: Record<string, unknown> | null | undefined): {
  budgetUsd: number;
  spentUsd: number;
  calls: number;
  pctUsed: number;
} | null {
  const mcp = (ext ?? {})['mcp'] as Record<string, unknown> | undefined;
  if (!mcp) return null;
  const budgetUsd = Number(mcp['budget_usd'] ?? 0);
  const spentUsd = Number(mcp['spent_usd'] ?? 0);
  const calls = Number(mcp['calls'] ?? 0);
  return {
    budgetUsd,
    spentUsd,
    calls,
    pctUsed: budgetUsd > 0 ? Math.min(100, (spentUsd / budgetUsd) * 100) : 0,
  };
}

/** É conector read-only? (capabilities.publish=false E read=true — ex.: Substack). */
export function isReadOnly(item: ConnectorCatalogItem): boolean {
  return item.capabilities?.publish === false && item.capabilities?.read === true;
}

/** Conector conectável? (não coming_soon/deprecated e com pelo menos 1 modo conectável). */
export function canConnectConnector(item: ConnectorCatalogItem): boolean {
  return (
    item.status !== 'coming_soon' &&
    item.status !== 'deprecated' &&
    item.supported_modes.some((m) => CONNECTABLE_MODES.includes(m))
  );
}
